<template>
  <div>
    <h3>
      <span>{{ $t('message.script_config_authentication') | title }}</span>
      <span class="wiki-link">
        <a :href="$t('message.script_config_authentication_doc_link')" target="_blank">
          WIKI
        </a>
      </span>
    </h3>

    <div v-if="willRunOnEachClusterNode" class="cluster-info">
      {{ $t('message.will_run_on_each_cluster_node') | title }}
    </div>
    <div v-if="willRunOnThisClusterNodeOnly" class="cluster-info">
      {{ $t('message.will_run_on_this_cluster_node_only') | title }}
    </div>

    <b-form>
      <b-form-row id="token-expiry-row">
        <div class="col-3">
          <span class="label label-default">
            {{ $t("message.auth_conf_token_expiry") | title }}
          </span>
        </div>
        <div class="col-4">
            <b-form-input v-model="tokenExpiryHours" type="range" min="0.1" max="168" step="0.1"
            :state="isNumber(tokenExpiryHours)"/>
        </div>
        <div class="col-1-half">
          <b-form-input v-model="tokenExpiryHours" @blur="convertTokenExpiryHours()"/>
          </div>
        <div class="col-1">
          {{ $t("message.hours") }}
        </div>
        <div class="col-1">
          <span class="label label-default" id="token-expiry-forced">
            {{ $t("message.auth_conf_token_expiry_forced") | title }}
          </span>
          <b-tooltip target="token-expiry-forced" triggers="hover">
            {{$t("message.auth_conf_token_expiry_forced_desc")}}
          </b-tooltip>
        </div>
        <div class="col-1">
          <div class="form-group form-check">
            <input type="checkbox" class="form-check-input" v-model="tokenExpiryForced"/>
          </div>
        </div>
      </b-form-row>

      <b-form-row id="passman-url-row">
        <div class="col-3">
          <span class="label label-default" :class="{'font-weight-bold': passmanUrlMandatory}">
            {{ $t("message.auth_conf_passman_url") | title }}<span v-if="passmanUrlMandatory">*</span>
          </span>
        </div>
        <div class="col-8">
            <b-form-input type="url" v-model="passmanUrl"/>
        </div>
      </b-form-row>

      <b-form-row>
        <b-col cols="3">
          <span class="label label-default">{{ $t("message.auth_conf_default_provider") | title }}</span>
        </b-col>
        <b-col cols="4">
          <b-form-select v-model="defaultProvider" :options="defaultProviderOptions"
            id="default-provider-select" />
        </b-col>
        <b-col cols="2">
          <b-dropdown :text="$t('message.button_new').toUpperCase()" variant="secondary">
            <b-dropdown-item @click="addAd">
              {{ $t("message.auth_conf_new_ad") | title }}
            </b-dropdown-item>
            <b-dropdown-item @click="addOpenLdap">
              {{ $t("message.auth_conf_new_open_ldap") | title }}
            </b-dropdown-item>
            <b-dropdown-item @click="addAzureAd">
              {{ $t("message.auth_conf_new_azure_ad") | title }}
            </b-dropdown-item>
            <b-dropdown-item @click="addJiraOauth2">
              {{ $t("message.auth_conf_new_jira_oauth2") | title }}
            </b-dropdown-item>
            <b-dropdown-item @click="addOracle">
              {{ $t("message.auth_conf_new_oracle") | title }}
            </b-dropdown-item>
            <b-dropdown-item @click="addSaml2Provider">
              {{ $t("message.auth_conf_new_saml2") | title }}
            </b-dropdown-item>
            <b-dropdown-item @click="addAtlassianCrowd">
              {{ $t("message.auth_conf_new_atl_crowd") | title }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <b-col cols="2" class="upper-save-button">
          <b-button class="padmin-button submit-button" @click="save"
            :disabled="saveDisabled" variant="primary">
            <b-spinner v-if="saveInProgress" small type="grow"/>
            {{$t('message.button_save') | title}}
          </b-button>
        </b-col>
      </b-form-row>

      <b-card v-for="(provider, provIndex) in providers" :key="index" class="provider-details">
        <b-form-row>
          <b-col cols="3">
            <a v-if="provider.type=='jira-oauth2-provider'" :title="$t('message.auth_conf_provider_jira_doc_label')"
              :href="$t('message.auth_conf_provider_jira_doc_link')" target="_blank">
              WIKI
            </a>
            <a v-if="provider.type=='azure-active-directory'" :title="$t('message.auth_conf_provider_azure_doc_label')"
              :href="$t('message.auth_conf_provider_azure_doc_link')" target="_blank">
              WIKI
            </a>
            <a v-if="provider.type=='active-directory-ldap'" :title="$t('message.auth_conf_provider_ad_doc_label')"
              :href="$t('message.auth_conf_provider_ad_doc_link')" target="_blank">
              WIKI
            </a>
            <a v-if="provider.type=='open-ldap'" :title="$t('message.auth_conf_provider_open_ldap_doc_label')"
              :href="$t('message.auth_conf_provider_open_ldap_doc_link')" target="_blank">
              WIKI
            </a>
            <a v-if="provider.type=='saml2-provider'" :title="$t('message.auth_conf_provider_saml_doc_label')"
              :href="$t('message.auth_conf_provider_saml_doc_link')" target="_blank">
              WIKI
            </a>
          </b-col>
          <b-col cols="5" class="heading">{{provider.type}}</b-col>
          <b-col>
            <button @click="delProvider(provIndex)" type="button" class="padmin-button del-button">
              {{$t('message.button_delete') | title}}
            </button>
          </b-col>
        </b-form-row>

        <b-form-row class="provider-name">
          <div class="col-3">
            <span class="label label-default font-weight-bold">{{ $t("message.auth_conf_provider_name") | title }}*</span>
          </div>
          <div class="col-8">
            <b-form-input type="text" v-model="provider.provider_name" required :state="provider.provider_name"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type.includes('ldap')">
          <div class="col-3">
            <span class="label label-default font-weight-bold">{{ $t("message.auth_conf_provider_servers") | title }}*</span>
          </div>
          <div class="col-8">
            <tags-input v-model="provider.servers" ref="ad-server-tags"
            :typeahead="false" placeholder="" :add-tags-on-comma="true" :add-tags-on-blur="true"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type.includes('ldap')">
          <div class="col-3">
            <span class="label label-default font-weight-bold">{{ $t("message.auth_conf_provider_base_dn") | title }}*</span>
          </div>
          <div class="col-8">
            <b-form-input type="text" v-model="provider.base_dn"
              :placeholder="$t('message.auth_conf_provider_base_dn_placeholder')"/>
          </div>
        </b-form-row>

        <b-form-row v-if='provider.type == "active-directory-ldap" && provider.base_dn.length > 0 &&
          (provider.base_dn[0] == "\"" || provider.base_dn[provider.base_dn.length-1] == "\"")'>
            <div class="offset-3 col-8 warning">
              {{$t('message.auth_conf_provider_base_dn_warning')}}
            </div>
        </b-form-row>

        <b-form-row v-if="provider.type.includes('ldap')">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_tech_user_username") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="text" v-model="provider.tech_user_username"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type.includes('ldap')">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_tech_user_password") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="password" v-model="provider.tech_user_password"
              :placeholder="provider.id == undefined ? '' : $t('message.no_change')"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type.includes('ldap')">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_certificate") | title }}
            </span>
          </div>
          <div class="col-8">
            <b-form-textarea class="form-control" v-model="provider.ca"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type.includes('ldap')">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_valid_names") | title }}
            </span>
          </div>
          <div class="col-8">
            <tags-input v-model="provider.valid_names" ref="ad-valid-names-tags"
            :typeahead="false" placeholder="" :add-tags-on-comma="true" :add-tags-on-blur="true"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type.includes('ldap')">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_visible_ous") | title }}
            </span>
          </div>
          <div class="col-8">
            <tags-input v-model="provider.visible_ous" ref="ous-tags" :typeahead="true"
              typeahead-activation-threshold="3" :add-tags-on-comma="true" :add-tags-on-blur="true"
              :existing-tags="provider.ou_options" :placeholder="(!provider.visible_ous || provider.visible_ous.length == 0) ?
                $t('message.auth_conf_provider_groups_all') + ', ' + $t('message.autocomplete_after_loading_ous') : ''"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'open-ldap'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_group_object_class") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="text" v-model="provider.group_object_class"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'open-ldap'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_group_member_attr") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="text" v-model="provider.group_member_attr"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'open-ldap'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_username_field") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="text" v-model="provider.username_field"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'jira-oauth2-provider' || provider.type == 'atlassian-crowd'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_tenant_name") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="text" v-model="provider.tenant_name"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'jira-oauth2-provider'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_tech_user") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="email" v-model="provider.tech_user"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'jira-oauth2-provider'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_app_token") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="text" v-model="provider.app_token"
              :placeholder="provider.id == undefined ? '' : $t('message.no_change')"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'azure-active-directory' || provider.type == 'oracle-idcs'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_tenant_name") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="text" v-model="provider.tenant_name"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'azure-active-directory' || provider.type == 'oracle-idcs' ||
          provider.type == 'atlassian-crowd'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_client_id") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="text" v-model="provider.client_id"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'azure-active-directory' || provider.type == 'oracle-idcs' ||
          provider.type == 'atlassian-crowd'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_credential") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="text" v-model="provider.credential"
              :placeholder="provider.id == undefined ? '' : $t('message.no_change')"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'azure-active-directory'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_redirect_uri") | title }}
            </span>
          </div>
          <div class="col-8">
            <b-form-input readonly v-model="provider.redirect_uri"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'azure-active-directory'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_is_sso") | title }}
            </span>
            <svg class="icon icon-info" id="sso-info"/>
            <b-tooltip target="sso-info" triggers="hover">
              {{$t("message.auth_conf_provider_is_sso_desc")}}
            </b-tooltip>
          </div>
          <div class="col-8">
            <div class="form-group form-check">
              <input type="checkbox" class="form-check-input" v-model="provider.is_sso"/>
            </div>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'oracle-idcs'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_base_url") | title }}
            </span>
          </div>
          <div class="col-8">
            <b-form-input v-model="provider.base_url"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'atlassian-crowd'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_port") | title }}
            </span>
          </div>
          <div class="col-8">
            <b-form-input type="number" v-model="provider.port"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'atlassian-crowd'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_context") | title }}
            </span>
          </div>
          <div class="col-8">
            <b-form-input v-model="provider.context"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type == 'atlassian-crowd'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_token_expiry_hours") | title }}
            </span>
          </div>
          <div class="col-6">
            <b-form-input type="range" v-model="provider.token_expiry_hours" min="0.5" max="24" step="0.5"/>
          </div>
          <div class="col-2">
            {{provider.token_expiry_hours}} {{$t('message.hours')}}
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type != 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_saml_auth") | title }}
            </span>
          </div>
          <div class="col-8">
            <div class="form-group form-check">
              <input type="checkbox" class="form-check-input" v-model="provider.saml_auth"
                @change="onSamlChange($event, provIndex)"/>
            </div>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.saml_auth || provider.type == 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_saml_entity_id") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input v-model="provider.saml2.entity_id"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.saml_auth || provider.type == 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_saml_sso") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input v-model="provider.saml2.sso"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.saml_auth || provider.type == 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_saml_slo") | title }}
            </span>
          </div>
          <div class="col-8">
            <b-form-input v-model="provider.saml2.slo"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.saml_auth || provider.type == 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_saml_sp_x509cert") | title }}
            </span>
          </div>
          <div class="col-8">
            <b-form-textarea v-model="provider.saml2.sp_x509cert" rows="3"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.saml_auth || provider.type == 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_saml_sp_private_key") | title }}
            </span>
          </div>
          <div class="col-8">
            <b-form-textarea v-model="provider.saml2.sp_private_key" rows="3"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.saml_auth || provider.type == 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_saml_lp_x509cert") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-textarea v-model="provider.saml2.lp_x509cert" rows=3
              :placeholder="provider.id == undefined ? '' : $t('message.no_change')"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.saml_auth || provider.type == 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default font-weight-bold">
              {{ $t("message.auth_conf_provider_saml_claims_upn") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input v-model="provider.claims.upn"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.saml_auth || provider.type == 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_saml_claims_first_name") | title }}
            </span>
          </div>
          <div class="col-8">
            <b-form-input v-model="provider.claims.first_name"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.saml_auth || provider.type == 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_saml_claims_last_name") | title }}
            </span>
          </div>
          <div class="col-8">
            <b-form-input v-model="provider.claims.last_name"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.saml_auth || provider.type == 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_saml_claims_email") | title }}
            </span>
          </div>
          <div class="col-8">
            <b-form-input v-model="provider.claims.email"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.saml_auth || provider.type == 'saml2-provider'">
          <div class="col-3">
            <span v-if="provider.type == 'saml2-provider'" class="label label-default font-weight-bold">
                {{ $t("message.auth_conf_provider_saml_claims_groups") | title }}*
            </span>
            <span v-else class="label label-default">
                {{ $t("message.auth_conf_provider_saml_claims_groups") | title }}*
            </span>
          </div>
          <div class="col-8">
            <b-form-input v-model="provider.claims.groups"/>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type != 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_required_groups") | title }}
            </span>
          </div>
          <div class="col-8" v-if="Object.keys(provider.auth_group_ids).length > 0 || !provider.required_groups">
            <tags-input v-if="(provider.group_options && Object.keys(provider.group_options).length > 0)
              || Object.keys(provider.auth_group_ids).length > 0" v-model="provider.auth_group_ids"
              :disabled="!provider.group_options || Object.keys(provider.group_options).length==0"
              :existing-tags="provider.group_options" only-existing-tags :typeahead="true" ref="group-tags"
              :typeahead-activation-threshold="3" :add-tags-on-comma="true"
              :placeholder="Object.keys(provider.auth_group_ids).length == 0 ? $t('message.auth_conf_provider_groups_none') : ''"/>
            <span v-else>{{$t('message.auth_conf_provider_groups_none') + ', ' + $t('message.edit_after_loading_groups')}}</span>
          </div>
          <div class="col-8" v-else-if="provider.required_groups">
            {{provider.required_groups.join(', ')}}
            <svg class='icon icon-delete' @click="deleteRequiredGroups(provIndex)">
              <title>{{$t("message.button_delete")}}</title>
            </svg>
          </div>
        </b-form-row>

        <b-form-row v-if="provider.type != 'saml2-provider'">
          <div class="col-3">
            <span class="label label-default">
              {{ $t("message.auth_conf_provider_used_groups") | title }}
            </span>
          </div>
          <div class="col-8" v-if="Object.keys(provider.permission_group_ids).length > 0 || !provider.used_groups">
            <tags-input v-if="(provider.group_options && Object.keys(provider.group_options).length > 0)
              || Object.keys(provider.permission_group_ids).length > 0" v-model="provider.permission_group_ids"
              :disabled="!provider.group_options || Object.keys(provider.group_options).length==0"
              :existing-tags="provider.group_options" only-existing-tags :typeahead="true" ref="group-tags"
              :typeahead-activation-threshold="3" :add-tags-on-comma="true"
              :placeholder="Object.keys(provider.permission_group_ids).length == 0 ? $t('message.auth_conf_provider_groups_all') : ''"/>
              <span v-else>{{$t('message.auth_conf_provider_groups_all') + ', ' + $t('message.edit_after_loading_groups')}}</span>
          </div>
          <div class="col-8" v-else-if="provider.used_groups">
            {{provider.used_groups.join(', ')}}
            <svg class='icon icon-delete' @click="deleteUsedGroups(provIndex)">
              <title>{{$t("message.button_delete")}}</title>
            </svg>
          </div>
        </b-form-row>

        <b-form-row>
          <b-button-group>
            <button v-if="provider.type != 'saml2-provider'" @click="loadGroupNames(provIndex)"
              type="button" class="padmin-button"
              :disabled="loadProvGroupsInProgress == provIndex || loadProvOusInProgress == provIndex">
              <b-spinner v-if="loadProvGroupsInProgress == provIndex" small type="grow"/>
              {{$t('message.button_load_group_names') | title}}
            </button>
            <button v-if="provider.type.includes('ldap')"
              @click="loadOuNames(provIndex)" type="button" class="padmin-button"
              :disabled="loadProvGroupsInProgress == provIndex || loadProvOusInProgress == provIndex">
              <b-spinner v-if="loadProvOusInProgress == provIndex" small type="grow"/>
              {{$t('message.button_load_ou_names') | title}}
            </button>
          </b-button-group>
        </b-form-row>

      </b-card>

    <div>
      <b-dropdown :text="$t('message.button_new').toUpperCase()" variant="secondary">
        <b-dropdown-item @click="addAd">
          {{ $t("message.auth_conf_new_ad") | title }}
        </b-dropdown-item>
        <b-dropdown-item @click="addOpenLdap">
          {{ $t("message.auth_conf_new_open_ldap") | title }}
        </b-dropdown-item>
        <b-dropdown-item @click="addAzureAd">
          {{ $t("message.auth_conf_new_azure_ad") | title }}
        </b-dropdown-item>
        <b-dropdown-item @click="addJiraOauth2">
          {{ $t("message.auth_conf_new_jira_oauth2") | title }}
        </b-dropdown-item>
        <b-dropdown-item @click="addOracle">
          {{ $t("message.auth_conf_new_oracle") | title }}
        </b-dropdown-item>
        <b-dropdown-item @click="addSaml2Provider">
          {{ $t("message.auth_conf_new_saml2") | title }}
        </b-dropdown-item>
        <b-dropdown-item @click="addAtlassianCrowd">
          {{ $t("message.auth_conf_new_atl_crowd") | title }}
        </b-dropdown-item>
      </b-dropdown>
      <b-button class="padmin-button submit-button" @click="save"
        :disabled="saveDisabled" variant="primary">
        <b-spinner v-if="saveInProgress" small type="grow"/>
        {{$t('message.button_save') | title}}
      </b-button>
    </div>
  </b-form>

  <b-alert v-model="showError" class="fixed-bottom" variant="danger" dismissible @dismissed="error=''; showError=false;">
    <div> {{ error }} </div>
  </b-alert>

  <ConsoleOutput v-if="result != null" :result="result" :output="output"/>

  <b-modal ref="check-response-modal" ok-only hide-header hide-footer>
    <div v-if="provInfoResponse">
      <div v-if="provInfoResponse.error" class="check-error">
        <div>
          {{$t('message.provider_check_error')}}:
        </div>
        <div>
          {{provInfoResponse.error.failure}}
        </div>
        <div>
          {{provInfoResponse.error.failure_details}}
        </div>
      </div>
      <div v-else>
        {{$t('message.provider_check_success')}}
        <div v-if="provInfoResponse.invalid_groups" class="check-error">
          {{$t('message.provider_check_invalid_groups')}}:
          {{provInfoResponse.invalid_groups.join(', ')}}
        </div>
      </div>
    </div>

    <b-modal-footer>
      <b-button @click="$refs['check-response-modal'].hide()">
        {{$t('message.button_close') | title}}
      </b-button>
    </b-modal-footer>
  </b-modal>

  </div>
</template>

<script>
import Vue from 'vue'
import {fromKeyValueToArray, arrayToKeyValue, dictToKeyValue, fromKeyValueToDict} from '../util'
import {get, post} from '../http';
import ConsoleOutput from './ConsoleOutput';
import {validateAlphanumericLower, validateTenantName, validateEmail, validateUrl} from '../util'

export default {
  name: "AuthenticationConfig",

  components: {
    ConsoleOutput
  },

  props: {},

  data() {
    return {
      providers: [],
      defaultProvider: "",
      tokenExpiryHours: 10,
      tokenExpiryForced: false,
      passmanUrl: "",
      result: null,
      output: "",
      restartResult: null,
      restartOutput: "",
      saveInProgress: false,
      loadProvGroupsInProgress: null,
      loadProvOusInProgress: null,
      provInfoResponse: null,
      ouNamesResponse: null,
      error: null,
      showError: false,
      nextProviderId: null,
      providerSensibleFields: ['tech_user_password', 'app_token', 'credential', 'lp_x509cert'],
      paramsOrigStr: '',
      defaultClaims: {
        upn: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/upn',
        first_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
        last_name: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
        email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
        groups: 'http://schemas.xmlsoap.org/claims/Group'
      }
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    defaultProviderOptions() {
      let defProvOptions = this.providers.map(prov => prov.provider_name)
      defProvOptions.unshift("local")
      return defProvOptions
    },

    passmanUrlMandatory() {
      return this.providers.filter(
        prov=>prov.type == 'azure-active-directory' || prov.type == 'jira-oauth2-provider' ||
           prov.type == 'oracle-idcs' || prov.type == 'saml2-provider'
      ).length > 0
    },

    saveDisabled() {
      if (this.saveInProgress) return true
      return this.paramsOrigStr == this.getParamsStr()
    }
  },

  methods: {
    getParamsStr() {
      return JSON.stringify({
        defaultProvider: this.defaultProvider,
        providers: this.providers,
        passmanUrl: this.passmanUrl,
        tokenExpiryHours: this.tokenExpiryHours,
        tokenExpiryForced: this.tokenExpiryForced
      })
    },

    isNumber(str) {
        let strt = String(str).trim()
        if (!strt) return false
        if (strt[strt.length - 1] == '.') return false
        return !isNaN(Number(strt))
    },

    convertTokenExpiryHours() {
      this.tokenExpiryHours = parseFloat(this.tokenExpiryHours)
      if (isNaN(this.tokenExpiryHours)) {
        this.tokenExpiryHours = 10
      }
      this.tokenExpiryHours = Math.min(168, Math.max(0.1, this.tokenExpiryHours))
    },

    getUrl() {
      return '/scripts/config/authentication'
    },

    load() {
      get(this.getUrl()).then(
        resp => {
          this.willRunOnEachClusterNode = resp.data.will_run_on_each_cluster_node
          this.willRunOnThisClusterNodeOnly = resp.data.will_run_on_this_cluster_node_only

          this.tokenExpiryForced = resp.data["params"]["auth_conf"]["value"]["token_expiry_forced"]

          let respTokenExpiry = resp.data["params"]["auth_conf"]["value"]["token_expiry"]
          if (respTokenExpiry){
            this.tokenExpiryHours = (respTokenExpiry / 3600)
          }

          this.passmanUrl = resp.data['params']['auth_conf']['value']['passman_url']

          this.providers = []
          let respProviders = resp.data['params']['auth_conf']['value']['providers']
          if(!resp.data['params']['auth_conf']['value']['next_provider_id']) {
            this.nextProviderId = 0
          } else {
            this.nextProviderId = resp.data['params']['auth_conf']['value']['next_provider_id']
            if (typeof this.nextProviderId === 'string' || this.nextProviderId instanceof String) {
              this.nextProviderId = parseInt(this.nextProviderId)
            }
          }
          for (const provName in respProviders) {
            let provider = respProviders[provName]
            if (provider.id == undefined) {
              provider.id = this.nextProviderId
              this.nextProviderId += 1
            }
            if (provider.is_new != undefined) {
              delete provider.is_new
            }

            provider.group_options = []
            provider.provider_name = provName
            if (provider.saml2 == undefined) {
              provider.saml2 = {}
            }
            if (provider.claims == undefined){
              provider.claims = {}
            }
            if (provider.auth_group_ids == undefined) {
              provider.auth_group_ids = []
            }
            if (provider.permission_group_ids == undefined) {
              provider.permission_group_ids = []
            }

            this.providerToKeyValue(provider)

            this.providers.push(provider)
          }

          if (resp.data['params']['auth_conf']['value'] && resp.data['params']['auth_conf']['value']['default_provider']) {
            this.defaultProvider = resp.data['params']['auth_conf']['value']['default_provider']
          } else {
            this.defaultProvider = 'local'
          }

          this.paramsOrigStr = this.getParamsStr()
        },
        err => {
          console.log("err", err)
        }
      )
    },

    updateGroupTags() {
      if (this.$refs['group-tags']) {
        for (let igrp=0; igrp < this.$refs['group-tags'].length; igrp++) {
          this.$refs['group-tags'][igrp].tagFromInput()
        }
      }
    },

    updateOuTags() {
      if (this.$refs['ou-tags']) {
        for (let iou=0; iou < this.$refs['ou-tags'].length; iou++) {
          this.$refs['ou-tags'][iou].tagFromInput()
        }
      }
    },

    onSamlChange(event, provIndex) {
      let provider = this.providers[provIndex]
      if (event.target.checked) {
        provider.claims = JSON.parse(JSON.stringify(this.defaultClaims))
      }
    },

    deleteUsedGroups(provIndex) {
      Vue.delete(this.providers[provIndex], 'used_groups')
    },

    deleteRequiredGroups(provIndex) {
      Vue.delete(this.providers[provIndex], 'required_groups')
    },

    save() {
      this.result = null
      this.output = ''
      this.showError = false

      this.updateGroupTags()
      this.updateOuTags()
      if (this.passmanUrlMandatory && this.passmanUrl == "") {
        this.error = this.$t('message.error_missing') + ' ' + this.$t('message.auth_conf_passman_url') + '!'
        this.showError = true
        return
      }
      this.convertTokenExpiryHours()

      let providersObj = {}
      let provNames = []
      for (let iProv = 0; iProv < this.providers.length; iProv++) {
        if (this.providers[iProv].type == 'azure-active-directory'){
          let redirectUri = ''
          if (this.passmanUrl) {
            if (!this.passmanUrl.startsWith('http')) {
              redirectUri = 'https://'
            }
            redirectUri += this.passmanUrl
            if (!this.passmanUrl.endsWith('/')) {
              redirectUri += '/'
            }
            redirectUri += 'redirect/' + this.providers[iProv].provider_name
            this.providers[iProv].redirect_uri = redirectUri
          }
        }

        let name = this.providers[iProv].provider_name
        if (!name) {
          this.error = this.$t('message.error_missing') + ' ' + this.$t('message.auth_conf_provider_name') + '!'
          this.showError = true
          return
        }

        if (provNames.includes(name)) {
          this.error = this.$t('message.auth_conf_provider_name') + ' ' + name +  ' ' + this.$t('message.duplicated') + '!'
          this.showError = true
          return
        }
        provNames.push(name)

        providersObj[name] = JSON.parse(JSON.stringify(this.providers[iProv]))
        let prov = providersObj[name]
        delete prov['group_options']
        delete prov['ou_options']

        this.providerFromKeyValue(prov)

        let requiredFields = [];
        if (prov.type == 'active-directory-ldap') {
          requiredFields = ['servers', 'base_dn', 'tech_user_username']
          if (prov.id == undefined) {
            requiredFields.push('tech_user_password')
          }
        } else if (prov.type == 'open-ldap') {
          requiredFields = ['servers', 'base_dn', 'tech_user_username', 'group_object_class',
            'group_member_attr', 'username_field']
          if (prov.id == undefined) {
            requiredFields.push('tech_user_password')
          }
        }
        else if (prov.type == 'azure-active-directory') {
          requiredFields = ['tenant_name', 'client_id']
          if (prov.id == undefined) {
            requiredFields.push('credential')
          }
        } else if (prov.type == 'oracle-idcs') {
          requiredFields = ['tenant_name', 'client_id', 'base_url']
          if (prov.id == undefined) {
            requiredFields.push('credential')
          }
        } else if (prov.type == 'jira-oauth2-provider') {
          requiredFields = ['tenant_name', 'tech_user']
          if (prov.id == undefined) {
            requiredFields.push('app_token')
          }
        } else if (prov.type == 'jira-oauth2-provider') {
          requiredFields = ['tenant_name', 'client_id']
          if (prov.id == undefined) {
            requiredFields.push('credential')
          }
        }

        for (const field of requiredFields) {
          if (prov[field] != undefined && (!prov[field] || prov[field].length == 0)) {
            let paramName = this.$t('message.auth_conf_provider_' + field)
            this.error = this.$t('message.auth_conf_provider_error_missing_param',
              {prov_name: name, param_name: paramName})
            this.showError = true
            return
          }
        }

        if (prov.saml_auth || prov.type == 'saml2-provider') {
          for (const samlField of ['entity_id', 'sso']) {
            if (!prov.saml2[samlField] || prov.saml2[samlField].length == 0) {
              let paramName = this.$t('message.auth_conf_provider_saml_' + samlField)
              this.error = this.$t('message.auth_conf_provider_error_missing_param',
                {prov_name: name, param_name: paramName})
              this.showError = true
              return
            }

            if (prov.id == undefined && (!prov.saml2['lp_x509cert'] || prov.saml2['lp_x509cert'].length == 0)) {
              let paramName = this.$t('message.auth_conf_provider_saml_lp_x509cert')
              this.error = this.$t('message.auth_conf_provider_error_missing_param',
                {prov_name: name, param_name: paramName})
              this.showError = true
              return
            }

            if (!prov.claims.upn || prov.claims.upn.length == 0) {
              let paramName = this.$t('message.auth_conf_provider_saml_claims_upn')
              this.error = this.$t('message.auth_conf_provider_error_missing_param',
                {prov_name: name, param_name: paramName})
              this.showError = true
              return
            }
          }

          if (prov.type == 'saml2-provider') {
            if (!prov.claims.groups || prov.claims.groups.length == 0) {
              let paramName = this.$t('message.auth_conf_provider_saml_groups')
              this.error = this.$t('message.auth_conf_provider_error_missing_param',
                {prov_name: name, param_name: paramName})
              this.showError = true
              return
            }
          }
        }

        if (prov.type == 'jira-oauth2-provider' || prov.type == 'azure-active-directory' ||
          prov.type == 'oracle-idcs' || prov.type == 'atlassian-crowd') {
          if (!validateAlphanumericLower(prov.provider_name)) {
            this.error = this.$t('message.error_invalid_providername') + ' ' + prov.provider_name
            this.showError = true
            return
          }
          if (prov.tenant_name.startsWith('https://')) {
            prov.tenant_name = prov.tenant_name.substring(8)
          }
          if (!validateTenantName(prov.tenant_name)) {
            this.error = this.$t('message.error_invalid_tenantname') + ' ' + prov.tenant_name
            this.showError = true
            return
          }
        }

        if (prov.type == 'jira-oauth2-provider' && !validateEmail(prov.tech_user)) {
          this.error = this.$t('message.error_invalid_tech_user') + ' ' + prov.tech_user
          this.showError = true
          return
        }

        if (prov.type == 'oracle-idcs' && !validateUrl(prov.base_url)) {
          this.error = this.$t('message.error_invalid_url') + ' ' + prov.base_url
          this.showError = true
          return
        }

        if (prov.type == 'atlassian-crowd' && prov.token_expiry_hours) {
          prov.token_expiry_seconds = parseInt(prov.token_expiry_hours) * 60 * 60
        }

        if (!prov.ca) {
          delete prov.ca
        }

        if (prov.id == undefined) {
          prov.id = this.nextProviderId
          this.nextProviderId += 1
        }
      }

      let data = {
        auth_conf: {
          providers: providersObj,
          default_provider: this.defaultProvider,
          token_expiry: this.tokenExpiryHours * 3600,
          token_expiry_forced: this.tokenExpiryForced,
          passman_url: this.passmanUrl,
          next_provider_id: this.nextProviderId
        }
      }

      this.saveInProgress = true

      post(this.getUrl(), data, {'Content-Type': 'application/json'}).then(
        resp => {
          this.result = resp.data.result
          this.output = resp.data.output
          if (resp.data.error) {
            this.error = resp.data.error
            this.showError = true
          }
          this.saveInProgress = false

          if (resp.data.agung_restart_required) {
            this.$root.$emit('bv::show::modal', 'passman-restart-modal-id')
          }
        },
        err => {
          this.saveInProgress = false
          console.log('err', err)
        }
      )
    },

    addAd() {
      this.providers.push({
        type: 'active-directory-ldap',
        provider_name: 'newAD',
        servers: [],
        base_dn: '',
        auth_group_ids: {},
        permission_group_ids: {},
        valid_names: [],
        tech_user_username: '',
        tech_user_password: '',
        ca: null,
        visible_ous: [],
        saml_auth: false,
        saml2: {},
        claims: {}
      })
    },

    addOpenLdap() {
      this.providers.push({
        type: 'open-ldap',
        provider_name: 'newOpenLDAP',
        servers: [],
        base_dn: '',
        auth_group_ids: {},
        permission_group_ids: {},
        valid_names: [],
        tech_user_username: '',
        tech_user_password: '',
        ca: null,
        visible_ous: [],
        group_object_class: '',
        group_member_attr: '',
        username_field: '',
        saml_auth: false,
        saml2: {},
        claims: {}
      })
    },

    addAzureAd() {
      this.providers.push({
        type: 'azure-active-directory',
        provider_name: 'azureprov',
        tenant_name: '',
        client_id: '',
        credential: '',
        auth_group_ids: {},
        permission_group_ids: {},
        saml_auth: false,
        saml2: {},
        claims: {}
      })
    },

    addOracle() {
      this.providers.push({
        type: 'oracle-idcs',
        provider_name: 'oracleprov',
        tenant_name: '',
        client_id: '',
        credential: '',
        base_url: '',
        auth_group_ids: {},
        permission_group_ids: {},
        saml_auth: false,
        saml2: {},
        claims: {}
      })
    },

    addJiraOauth2() {
      this.providers.push({
        type: 'jira-oauth2-provider',
        provider_name: 'jiracloud',
        tenant_name: '',
        tech_user: '',
        app_token:'',
        auth_group_ids: {},
        permission_group_ids: {},
        saml_auth: false,
        saml2: {},
        claims: {}
      })
    },

    addSaml2Provider() {
      this.providers.push({
        type: 'saml2-provider',
        provider_name: 'saml2prov',
        saml2: {
          entity_id: '',
          sso: '',
          slo:'',
          lp_x509cert: ''
        },
        claims: JSON.parse(JSON.stringify(this.defaultClaims))
      })
    },

    addAtlassianCrowd() {
      this.providers.push({
        type: 'atlassian-crowd',
        provider_name: 'crowdprov',
        tenant_name: '',
        client_id: '',
        credential: '',
        port: '',
        context: '',
        token_expiry_hours: '8',
        token_expiry_seconds: '28800',
        auth_group_ids: {},
        permission_group_ids: {},
        saml_auth: false,
        saml2: {},
        claims: {}
      })
    },

    delProvider(provIndex) {
      let providerName = this.providers[provIndex].provider_name
      this.providers.splice(provIndex, 1)
      if (this.defaultProvider === providerName) {
        this.defaultProvider = 'local'
      }
    },

    providerFromKeyValue(prov) {
      prov.servers = fromKeyValueToArray(prov.servers)
      prov.valid_names = fromKeyValueToArray(prov.valid_names)
      prov.visible_ous = fromKeyValueToArray(prov.visible_ous)
      prov.auth_group_ids = fromKeyValueToDict(prov.auth_group_ids)
      prov.permission_group_ids = fromKeyValueToDict(prov.permission_group_ids)
    },

    providerToKeyValue(prov) {
      prov.visible_ous = arrayToKeyValue(prov.visible_ous)
      prov.servers = arrayToKeyValue(prov.servers)
      prov.valid_names = arrayToKeyValue(prov.valid_names)
      prov.auth_group_ids = dictToKeyValue(prov.auth_group_ids)
      prov.permission_group_ids = dictToKeyValue(prov.permission_group_ids)
    },

    prepareLoadProviderInfo(provIndex) {
      if (this.passmanUrlMandatory && !this.passmanUrl) {
        this.provInfoResponse = {error: {failure: this.$t('message.error_missing') + ' ' + this.$t('message.auth_conf_passman_url') + '!'}}
        this.$refs['check-response-modal'].show()
        return
      }
      delete this.providers[provIndex]['group_options']
      delete this.providers[provIndex]['ou_options']
      let provider = JSON.parse(JSON.stringify(this.providers[provIndex]))
      this.providerFromKeyValue(provider)
      Object.keys(provider).forEach(key => {
        if (!['tech_user_password', 'app_token', 'credential', 'lp_x509cert'].includes(key)) {
          if (provider[key] == undefined || provider[key] === '' ||
            (Array.isArray(provider[key]) && provider[key].length == 0)) {
            delete provider[key]
          }
        }
      })

      return provider
    },

    async loadGroupNames(provIndex) {
      let provider = this.prepareLoadProviderInfo(provIndex)
      provider.passman_url = this.passmanUrl
      this.loadProvGroupsInProgress = provIndex

      post('/provider_groups', provider).then(
        resp => {
          this.provInfoResponse = resp.data
          this.loadProvGroupsInProgress = null
          this.providers[provIndex].group_options = []
          if (!this.provInfoResponse.error && this.provInfoResponse.provider_groups_with_ids) {
            this.providers[provIndex].group_options = dictToKeyValue(this.provInfoResponse.provider_groups_with_ids)
          }
          this.$refs['check-response-modal'].show()
        },
        err => {
          console.log('err', err)
          this.loadProvGroupsInProgress = null
          this.provInfoResponse = {'error': err}
          this.$refs['check-response-modal'].show()
        }
      )
    },

    async loadOuNames(provIndex) {
      let provider = this.prepareLoadProviderInfo(provIndex)
      this.loadProvOusInProgress = provIndex

      post('/provider_ous', provider).then(
        resp => {
          this.provInfoResponse = resp.data
          this.loadProvOusInProgress = null
          if (!this.provInfoResponse.error && this.provInfoResponse.provider_ous) {
            this.providers[provIndex].ou_options = []
            for(let iOu=0; iOu < this.provInfoResponse.provider_ous.length; iOu++) {
              let ouName = this.provInfoResponse.provider_ous[iOu]
              this.providers[provIndex].ou_options.push({key: ouName, value: ouName})
            }
          }
          this.$refs['check-response-modal'].show()
        },
        err => {
          console.log('err', err)
          this.loadProvOusInProgress = null
          this.provInfoResponse = {'error': err}
          this.$refs['check-response-modal'].show()
        }
      )
    }
  }
}
</script>
<style scoped>
  @import "./../styles/authenticationConfigComponent.css"
</style>
